import { useEffect, useMemo, useState } from 'react'
import { Check, Expand, Loader2, Printer, Trash2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { useBlocoColleagues } from '@/hooks/useBlocoColleagues'
import type { BlocoDetail, BlocoLabel } from '@/types/bloco'
import { BlocoColleagueSelect } from './BlocoColleagueSelect'
import { BlocoLabelPicker } from './BlocoLabelPicker'
import { BlocoLabelChip } from './BlocoLabelChip'
import { BlocoThreadMessages } from './BlocoThreadMessages'
import { BlocoAttachmentField } from './BlocoAttachmentField'
import { BlocoAttachmentsList } from './BlocoAttachmentsList'
import { printBlocoDetail } from './blocoPrint'
import { formatBlocoDueLabel, formatMemberStatusLabel } from './blocosUtils'

interface BlocoThreadModalProps {
  open: boolean
  detail: BlocoDetail | null
  loading: boolean
  saving: boolean
  currentUserId?: string
  onClose: () => void
  onComplete: (id: string) => void
  onDelete: (id: string) => void
  onSendMessage: (id: string, body: string) => Promise<unknown>
  onSaveMembers: (id: string, memberUserIds: string[]) => Promise<unknown>
  onUploadAttachment: (id: string, file: File) => Promise<unknown>
  labels?: BlocoLabel[]
  onAssignLabels?: (id: string, labelIds: string[]) => void
  onCreateLabel?: (name: string, color: string) => Promise<unknown>
}

const BODY_PREVIEW_LENGTH = 220

export function BlocoThreadModal({
  open,
  detail,
  loading,
  saving,
  currentUserId,
  onClose,
  onComplete,
  onDelete,
  onSendMessage,
  onSaveMembers,
  onUploadAttachment,
  labels = [],
  onAssignLabels,
  onCreateLabel,
}: BlocoThreadModalProps) {
  const [messageBody, setMessageBody] = useState('')
  const [memberUserIds, setMemberUserIds] = useState<string[]>([])
  const [bodyExpanded, setBodyExpanded] = useState(false)
  const [threadAttachments, setThreadAttachments] = useState<File[]>([])
  const { colleagues, loading: colleaguesLoading } = useBlocoColleagues(open)

  const isOwner = detail?.userId === currentUserId
  const dueLabel = detail ? formatBlocoDueLabel(detail) : null
  const awaitingYou = detail && currentUserId && detail.pendingUserId === currentUserId
  const bodyPreview =
    detail && detail.body.length > BODY_PREVIEW_LENGTH
      ? `${detail.body.slice(0, BODY_PREVIEW_LENGTH).trim()}…`
      : detail?.body

  useEffect(() => {
    if (!detail) {
      setMemberUserIds([])
      return
    }
    setMemberUserIds(detail.members.map((member) => member.id).filter((id) => id !== detail.userId))
    setBodyExpanded(false)
    setThreadAttachments([])
  }, [detail])

  const memberChips = useMemo(() => detail?.members ?? [], [detail])

  const handleSend = async () => {
    if (!detail) return
    const trimmed = messageBody.trim()
    if (!trimmed) return
    await onSendMessage(detail.id, trimmed)
    setMessageBody('')
  }

  const handleSaveMembers = async () => {
    if (!detail || !isOwner) return
    const payload = [detail.userId, ...memberUserIds]
    await onSaveMembers(detail.id, payload)
  }

  const handleUploadThreadAttachments = async () => {
    if (!detail || threadAttachments.length === 0) return
    for (const file of threadAttachments) {
      await onUploadAttachment(detail.id, file)
    }
    setThreadAttachments([])
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title='Conversa do bloco'
        description={
          detail
            ? `${detail.title} · Criado por ${detail.author.name}`
            : 'Detalhes e mensagens internas'
        }
        size='2xl'
        className='max-h-[min(780px,94vh)]'
      >
        {!detail && loading ? (
          <div className='flex flex-col items-center justify-center gap-2 py-10 text-text-muted'>
            <Loader2 size={24} className='animate-spin' />
            <p className='text-sm'>Abrindo bloco…</p>
          </div>
        ) : detail ? (
          <div className='space-y-4'>
            <div className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4'>
              <h3 className='text-base font-semibold text-text-primary'>{detail.title}</h3>
              <p className='mt-1 text-sm text-text-muted'>{detail.subject}</p>
              {detail.labels && detail.labels.length > 0 && (
                <div className='mt-2 flex flex-wrap gap-1'>
                  {detail.labels.map((label) => (
                    <BlocoLabelChip key={label.id} label={label} />
                  ))}
                </div>
              )}
              {detail.body.trim() && (
                <>
                  <p className='mt-3 whitespace-pre-wrap text-sm leading-relaxed text-text-primary'>
                    {bodyPreview}
                  </p>
                  {detail.body.length > BODY_PREVIEW_LENGTH && (
                    <div className='mt-2 flex justify-end'>
                      <Button
                        size='sm'
                        variant='secondary'
                        icon={<Expand size={14} />}
                        onClick={() => setBodyExpanded(true)}
                      >
                        Abrir detalhes
                      </Button>
                    </div>
                  )}
                </>
              )}
              <div className='mt-3 flex flex-wrap items-center gap-2 text-xs text-text-muted'>
                {awaitingYou && detail.status !== 'done' && (
                  <span className='rounded-md bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-600'>
                    Aguardando sua resposta
                  </span>
                )}
                {detail.isShared && detail.status !== 'done' && (
                  <span className='rounded-md bg-bg-card px-2 py-0.5 font-medium'>
                    Bloco em grupo
                  </span>
                )}
                {detail.pendingUser && !awaitingYou && detail.status !== 'done' && (
                  <span className='rounded-md bg-bg-card px-2 py-0.5 font-medium'>
                    Próximo: {detail.pendingUser.name}
                  </span>
                )}
                {dueLabel && (
                  <span className='rounded-md bg-bg-card px-2 py-0.5 font-medium'>{dueLabel}</span>
                )}
                {memberChips.map((member) => (
                  <span
                    key={member.id}
                    className='inline-flex items-center gap-1 rounded-full bg-[var(--brand-primary-muted)]/40 px-2.5 py-0.5 font-medium text-[var(--brand-primary)]'
                  >
                    {member.name}
                    <span className='text-[10px] opacity-80'>
                      · {formatMemberStatusLabel(member.status)}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            {(detail.attachments?.length ?? 0) > 0 && (
              <section className='space-y-2'>
                <h3 className='text-xs font-semibold uppercase tracking-wide text-text-muted'>Anexos</h3>
                <BlocoAttachmentsList attachments={detail.attachments ?? []} />
              </section>
            )}

            {isOwner && onAssignLabels && onCreateLabel && (
              <section className='space-y-2'>
                <h3 className='text-xs font-semibold uppercase tracking-wide text-text-muted'>Etiquetas</h3>
                <BlocoLabelPicker
                  labels={labels}
                  selectedIds={detail.labels?.map((l) => l.id) ?? []}
                  onToggle={(labelId) => {
                    const current = detail.labels?.map((l) => l.id) ?? []
                    const next = current.includes(labelId)
                      ? current.filter((id) => id !== labelId)
                      : [...current, labelId]
                    onAssignLabels(detail.id, next)
                  }}
                  onCreate={onCreateLabel}
                  compact
                />
              </section>
            )}

            <section className='space-y-2'>
              <h3 className='text-xs font-semibold uppercase tracking-wide text-text-muted'>Mensagens</h3>
              <BlocoThreadMessages messages={detail.messages} loading={loading} />
            </section>

          {detail.status !== 'done' && (
            <div className='space-y-2'>
              <BlocoAttachmentField
                files={threadAttachments}
                onChange={setThreadAttachments}
                disabled={saving}
              />
              {threadAttachments.length > 0 && (
                <div className='flex justify-end'>
                  <Button
                    size='sm'
                    variant='secondary'
                    disabled={saving}
                    onClick={() => void handleUploadThreadAttachments()}
                  >
                    Enviar anexos
                  </Button>
                </div>
              )}
              <textarea
                  value={messageBody}
                  onChange={(event) => setMessageBody(event.target.value)}
                  placeholder='Escreva uma mensagem para a equipe…'
                  rows={4}
                  className='w-full resize-none rounded-lg border border-[var(--glass-border)] bg-bg-card px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-muted focus:border-[var(--brand-primary)]'
                />
                <div className='flex justify-end'>
                  <Button
                    size='sm'
                    onClick={() => void handleSend()}
                    disabled={saving || !messageBody.trim()}
                    icon={saving ? <Loader2 size={14} className='animate-spin' /> : undefined}
                  >
                    Enviar mensagem
                  </Button>
                </div>
              </div>
            )}

            {isOwner && (
              <section className='space-y-2 border-t border-[var(--glass-border)] pt-4'>
                <BlocoColleagueSelect
                  colleagues={colleagues}
                  selectedIds={memberUserIds}
                  currentUserId={currentUserId}
                  loading={colleaguesLoading}
                  onChange={setMemberUserIds}
                />
                <div className='flex justify-end'>
                  <Button size='sm' variant='secondary' onClick={() => void handleSaveMembers()} disabled={saving}>
                    Salvar colegas
                  </Button>
                </div>
              </section>
            )}

          <div className='flex flex-wrap justify-end gap-2 border-t border-[var(--glass-border)] pt-4'>
            <Button
              size='sm'
              variant='secondary'
              onClick={() => detail && printBlocoDetail(detail)}
              icon={<Printer size={14} />}
            >
              Imprimir
            </Button>
            {detail.status !== 'done' && (
                <Button
                  size='sm'
                  variant='secondary'
                  onClick={() => onComplete(detail.id)}
                  icon={<Check size={14} />}
                >
                  Concluir
                </Button>
              )}
              {isOwner && (
                <Button
                  size='sm'
                  variant='destructive'
                  onClick={() => onDelete(detail.id)}
                  icon={<Trash2 size={14} />}
                >
                  Excluir
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={bodyExpanded}
        onClose={() => setBodyExpanded(false)}
        title='Detalhes do bloco'
        description={detail ? `${detail.title} · ${detail.subject}` : undefined}
        size='2xl'
      >
        {detail && (
          <div className='space-y-3'>
            <div>
              <p className='text-xs font-medium uppercase tracking-wide text-text-muted'>Título</p>
              <p className='text-sm font-semibold text-text-primary'>{detail.title}</p>
            </div>
            <div>
              <p className='text-xs font-medium uppercase tracking-wide text-text-muted'>Assunto</p>
              <p className='text-sm text-text-primary'>{detail.subject}</p>
            </div>
            {detail.body.trim() && (
              <div>
                <p className='text-xs font-medium uppercase tracking-wide text-text-muted'>Detalhes</p>
                <p className='whitespace-pre-wrap text-sm leading-relaxed text-text-primary'>{detail.body}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  )
}
